<template>
  <div
    v-if="entities.length > 0"
    class="mt-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
  >
    <div class="flex flex-wrap gap-x-4 gap-y-1.5">
      <a
        v-for="(entity, idx) in visibleEntities"
        :key="idx"
        href="#"
        class="flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs transition hover:bg-gray-200"
        @click.prevent="navigateToEntity(entity)"
      >
        <Icon
          :name="entityIcon(entity)"
          class="size-3.5 shrink-0"
          :class="entityIconColor(entity)"
        />
        <span class="text-gray-500">{{ entityLabel(entity) }}</span>
        <span class="font-medium text-gray-700">{{ entity.name }}</span>
      </a>
    </div>
    <button
      v-if="entities.length > MAX_VISIBLE"
      type="button"
      :aria-expanded="expanded"
      class="mt-1.5 text-xs font-medium text-purple-600 hover:text-purple-800"
      @click="expanded = !expanded"
    >
      {{
        expanded ? "Show less" : `Show ${entities.length - MAX_VISIBLE} more`
      }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from "vue";
import { useRouter, type RouteLocationRaw } from "vue-router";
import Icon from "@/components/Core/Icon.vue";
import { AIEntityRef } from "@/composables/useMarkdown";
import type { IconName } from "@/services/icons";
import { useDataFiltersStore } from "@/store/data_filters";

const props = defineProps({
  entities: {
    type: Array as PropType<AIEntityRef[]>,
    required: true,
  },
});

const router = useRouter();
const dataFiltersStore = useDataFiltersStore();
const MAX_VISIBLE = 4;
const expanded = ref(false);

const visibleEntities = computed(() =>
  expanded.value ? props.entities : props.entities.slice(0, MAX_VISIBLE),
);

const CHANNEL_ICONS: Record<string, { icon: IconName; color: string }> = {
  google_ads: { icon: "google-ads", color: "" },
  facebook_ads: { icon: "facebook", color: "text-facebook-blue" },
  linkedin_ads: { icon: "linked-in", color: "text-linkedin-blue" },
  snapchat_ads: { icon: "snapchat-ghost", color: "" },
  tiktok_ads: { icon: "tik-tok", color: "text-black" },
  bing_ads: { icon: "bing", color: "text-bing-teal" },
  instagram_organic: { icon: "instagram", color: "" },
  facebook_organic: { icon: "facebook", color: "text-facebook-blue" },
  linkedin_organic: { icon: "linked-in", color: "text-linkedin-blue" },
  tiktok_organic: { icon: "tik-tok", color: "text-black" },
  youtube: { icon: "youtube", color: "text-youtube-red" },
  google_analytics_4: { icon: "google-analytics", color: "" },
  google_search_console: { icon: "browser", color: "" },
};

const TYPE_ICONS: Record<string, { icon: IconName; color: string }> = {
  campaign: { icon: "ad-solid", color: "text-gray-500" },
  page: { icon: "browser", color: "text-gray-500" },
  search_term: { icon: "search-dollar", color: "text-gray-500" },
  resource: { icon: "chart-box", color: "text-gray-500" },
  dashboard: { icon: "chart-box", color: "text-purple-500" },
  goal: { icon: "bullseye-arrow", color: "text-purple-500" },
  kpi: { icon: "bullseye-arrow", color: "text-purple-500" },
};

const TYPE_LABELS: Record<string, string> = {
  resource: "Account",
  campaign: "Campaign",
  page: "Page",
  search_term: "Search term",
  dashboard: "Dashboard",
  goal: "Goal",
  kpi: "KPI",
};

const CHANNEL_ROUTES: Record<string, string> = {
  google_ads: "ad-performance-overview",
  facebook_ads: "ad-performance-overview",
  linkedin_ads: "ad-performance-overview",
  snapchat_ads: "ad-performance-overview",
  tiktok_ads: "ad-performance-overview",
  bing_ads: "ad-performance-overview",
  instagram_organic: "social-media-account-summary",
  facebook_organic: "social-media-account-summary",
  linkedin_organic: "social-media-account-summary",
  tiktok_organic: "social-media-account-summary",
  youtube: "social-media-account-summary",
  google_analytics_4: "website-analytics",
  google_search_console: "page-insights-search",
};

function entityIcon(entity: AIEntityRef): IconName {
  if (entity.channel && CHANNEL_ICONS[entity.channel]) {
    return CHANNEL_ICONS[entity.channel].icon;
  }
  return TYPE_ICONS[entity.type]?.icon ?? "tag";
}

function entityIconColor(entity: AIEntityRef): string {
  if (entity.channel && CHANNEL_ICONS[entity.channel]) {
    return CHANNEL_ICONS[entity.channel].color || "text-gray-500";
  }
  return TYPE_ICONS[entity.type]?.color ?? "text-gray-500";
}

function entityLabel(entity: AIEntityRef): string {
  return TYPE_LABELS[entity.type] ?? "";
}

function entityRoute(entity: AIEntityRef): RouteLocationRaw {
  if (entity.type === "campaign") {
    return {
      name: "ad-performance-overview",
      query: entity.id
        ? { level: "campaign", id: String(entity.id) }
        : { tableSearch: entity.name },
    };
  }

  if (entity.type === "page") {
    return entity.id
      ? { name: "page-insights-content", params: { pk: String(entity.id) } }
      : { name: "page-insights-search", query: { tableSearch: entity.name } };
  }

  if (entity.type === "search_term") {
    return entity.id
      ? { name: "search-term-detail", params: { id: String(entity.id) } }
      : { name: "search-term-report" };
  }

  if (entity.type === "dashboard") {
    return { name: "view-dashboard", params: { pk: String(entity.id) } };
  }

  if (entity.type === "goal") {
    return { name: "goal-details", params: { pk: String(entity.id) } };
  }

  if (entity.type === "kpi") {
    return { name: "kpi_details", params: { pk: String(entity.id) } };
  }

  // Resource — route based on channel
  if (entity.channel && CHANNEL_ROUTES[entity.channel]) {
    return { name: CHANNEL_ROUTES[entity.channel] };
  }

  return { name: "ad-performance-overview" };
}

function navigateToEntity(entity: AIEntityRef): void {
  if (entity.type === "resource" && entity.id) {
    dataFiltersStore.includedResources = [entity.id];
  }
  router.push(entityRoute(entity));
}
</script>
